import type { FitRecordedMetadata } from './fitRecordedMetrics';
import { normalizeFitMetricName } from './fitRecordedMetrics';
import type { StreamPoint } from './workoutStream';

export type SensorRange = { min: number | null; avg: number | null; max: number | null };
export type EnvironmentRanges = { altitude: SensorRange | null; temperature: SensorRange | null };

/** Selected ranges use only their own samples; never substitute whole-workout values. */
export function workoutEnvironmentRanges(snapshot?: FitRecordedMetadata | null, stream: StreamPoint[] = [], selection?: { start: number; end: number } | null): EnvironmentRanges {
  const samples = selection ? stream.slice(Math.min(selection.start, selection.end), Math.max(selection.start, selection.end) + 1) : stream;
  const range = (name: 'altitude' | 'temperature'): SensorRange | null => {
    const values = samples.map(point => point[name]).filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    if (values.length) {
      let min = Infinity, max = -Infinity, sum = 0;
      for (const value of values) { min = Math.min(min, value); max = Math.max(max, value); sum += value; }
      return { min, max, avg: sum / values.length };
    }
    if (selection) return null;
    const metric = snapshot?.metrics.find(item => normalizeFitMetricName(item.name) === name);
    const finite = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : null;
    if (!metric) return null;
    const result = { min: finite(metric.min), avg: finite(metric.avg), max: finite(metric.max) };
    return Object.values(result).some(value => value != null) ? result : null;
  };
  return { altitude: range('altitude'), temperature: range('temperature') };
}
